import { z } from 'zod';

/**
 * Chargeback no payment do app (#740) — a regra ÚNICA de como o estado das
 * disputas de uma cobrança vira dinheiro.
 *
 * Quem escreve é o `handleChargeDispute` (Functions, `charge.dispute.*`); quem
 * lê é o CRM (`crm-functions`), para tratar o chargeback como estorno da
 * comissão. Os dois chamam as funções daqui — o app para derivar os campos
 * agregados que grava, o CRM para decidir sobre eles —, então não existe uma
 * segunda cópia da tabela de status em lugar nenhum.
 *
 * **Uma cobrança pode ter mais de uma disputa** (o Stripe abre uma nova quando
 * o emissor contesta de novo). Por isso o estado vive POR DISPUTA, no mapa
 * `payment.disputes.{dp_id}`, e os campos planos (`disputed_amount`,
 * `dispute_status`, `dispute_id`, `dispute_updated_at`) são DERIVADOS dele.
 * Até esta versão só existiam os planos, e duas disputas se sobrescreviam
 * (`dp_1` perdida + `dp_2` ganha deixava `disputed_amount: 0`). Payment gravado
 * no formato antigo é lido como uma disputa só — ver `paymentDisputeEntries`.
 *
 * Origem: #740 — 2026-09-25 23:29 +0200.
 *
 * Usado por: Functions `billing/webhooks/handleChargeDispute.ts` (grava);
 * CRM Functions `lib/commissionCycle.ts` (o maior entre refund e disputa vira
 * estorno da comissão — nunca a soma) e `lib/appPayments.ts`; `schemas.ts`
 * (`PaymentSchema.disputes`). Só servidor — nenhum cliente lê disputa.
 *
 * Arquitetura: tudo puro e tolerante a doc cru (`unknown` em todo campo):
 * esta regra roda sobre `snap.data()` sem passar por `PaymentSchema`, para um
 * payment legado com campo estranho não derrubar o cálculo da comissão.
 *
 * Dados — `tenants/{tenantId}/payments/{paymentId}`, só os campos lidos:
 *   { "amount": 19990,
 *     "disputes": { "dp_000000000001": { "status": "lost", "amount": 19990,
 *                                        "updated_at": 1790000000000 },
 *                   "dp_000000000002": { "status": "won",  "amount": 19990,
 *                                        "updated_at": 1790000100000 } },
 *     "disputed_amount": 19990, "dispute_status": "lost",
 *     "dispute_id": "dp_000000000001", "dispute_updated_at": 1790000100000 }
 * Valores em CENTS. Os quatro campos planos são DERIVADOS do mapa por
 * `summarizePaymentDisputes` e ficam gravados para quem lê sem esta regra
 * (admin, agregação). Schema: `PaymentSchema` em `schemas.ts`.
 *
 * Issues: #740 (chargeback no CRM), #679 (venda assistida — a comissão que
 * isto protege), #795 (BIL-2, auditoria).
 */

/**
 * Uma disputa, como fica gravada em `payment.disputes.{disputeId}`.
 *
 * Origem: #740 — 2026-09-25 23:29 +0200.
 *
 * Dados: `tenants/{tenantId}/payments/{paymentId}.disputes.{dp_…}` — exemplo
 * no cabeçalho do módulo. `amount` em CENTS; `status` é texto livre do Stripe
 * (o enum seria a armadilha: status novo do fornecedor derrubaria o parse).
 * Escrito só pelo `handleChargeDispute` (Admin SDK).
 *
 * Usado por: `PaymentSchema.disputes` (`schemas.ts`, `z.record`) e
 * `paymentDisputeEntries` (parse tolerante entrada a entrada). Não leva
 * `.catch()`: entrada ilegível é IGNORADA pelo leitor, não substituída.
 */
export const PaymentDisputeEntrySchema = z.object({
  /** `status` cru do Stripe (vocabulário do fornecedor, ganha valores novos). */
  status: z.string().min(1),
  /** Valor da disputa (cents), já limitado ao `amount` do payment. Guardado
   *  cru, qualquer que seja o status: quem decide se ele conta é
   *  `classifyDisputeStatus`, não quem grava. */
  amount: z.number().int().nonnegative(),
  /** `event.created` (ms) da última entrega aplicada a ESTA disputa. */
  updated_at: z.number().int().nonnegative(),
});
export type PaymentDisputeEntry = z.infer<typeof PaymentDisputeEntrySchema>;

/**
 * O que um status de disputa significa para o dinheiro.
 *
 * - `OPEN` — o valor saiu da conta e ainda pode voltar (`needs_response`,
 *   `under_review` e **qualquer status que o Stripe venha a criar**). Conta
 *   como estorno, mas é reversível.
 * - `LOST` — o valor saiu e não volta. Estorno definitivo.
 * - `WON` — o lojista ganhou; o valor voltou.
 * - `NO_FUNDS` — nada foi retido: `warning_*` (consulta prévia do emissor, que
 *   vira outro status se escalar) e `prevented` (resolvida antes do chargeback;
 *   quando devolve, devolve por REFUND, que chega por `charge.refunded` —
 *   contar a disputa também somaria o mesmo dinheiro duas vezes).
 *
 * Status desconhecido cai em `OPEN` de propósito: o efeito no CRM é reversível
 * (disputa ganha reinstala a comissão), e pagar comissão sobre dinheiro que
 * saiu não é.
 */
export type DisputeClass = 'OPEN' | 'LOST' | 'WON' | 'NO_FUNDS';

const NO_FUNDS_STATUSES: ReadonlySet<string> = new Set([
  'prevented',
  'warning_needs_response',
  'warning_under_review',
  'warning_closed',
]);

/**
 * Classifica o `status` cru do Stripe na tabela de `DisputeClass` acima.
 *
 * Origem: #740 — 2026-09-25 23:29 +0200.
 *
 * Usado por: `summarizePaymentDisputes` (este módulo). Exportado, mas sem
 * consumidor externo em 2026-10-04 — o CRM decide pelo resumo, não pela classe.
 */
export function classifyDisputeStatus(status: string): DisputeClass {
  if (status === 'lost') return 'LOST';
  if (status === 'won') return 'WON';
  if (NO_FUNDS_STATUSES.has(status)) return 'NO_FUNDS';
  return 'OPEN';
}

/**
 * `won`/`lost`: o desfecho do Stripe; nada mais muda o dinheiro da disputa.
 *
 * Origem: #740 — 2026-09-25 23:29 +0200.
 *
 * Usado por: Functions `webhooks/handleChargeDispute.ts` — evento que chega
 * DEPOIS de um desfecho (fora de ordem, reenvio) não reabre a disputa.
 */
export function isTerminalDisputeStatus(status: string | undefined | null): boolean {
  return status === 'won' || status === 'lost';
}

/**
 * Prioridade do `dispute_status` AGREGADO (maior = aparece). A regra: o status
 * que **ainda mexe no dinheiro** vence. Uma disputa aberta vence tudo, porque é
 * a única que pode mudar o valor e é ela que bloqueia decisões no CRM; depois a
 * perdida (dinheiro que saiu de vez); depois as sem dinheiro retido, com a
 * consulta prévia à frente da prevenida (ela ainda pode escalar); a ganha por
 * último. Empate de classe desempata pela disputa mais recente.
 */
const AGGREGATE_PRIORITY: Readonly<Record<DisputeClass, number>> = {
  OPEN: 4,
  LOST: 3,
  NO_FUNDS: 2,
  WON: 1,
};

export type IdentifiedDisputeEntry = PaymentDisputeEntry & { id: string };

/** Campos do payment que esta regra lê — tudo `unknown`: leitura de doc cru. */
export type PaymentDisputeFields = {
  amount?: unknown;
  disputes?: unknown;
  disputed_amount?: unknown;
  dispute_status?: unknown;
  dispute_id?: unknown;
  dispute_updated_at?: unknown;
};

/** Id sintético da disputa lida do formato antigo sem `dispute_id`. */
export const LEGACY_DISPUTE_ID = '__legacy__';

/**
 * As disputas do payment, uma entrada por disputa.
 *
 * Com o mapa `disputes`, ele é a verdade (entrada que não parseia é ignorada:
 * leitura tolerante). Sem ele, os campos planos do formato anterior viram UMA
 * disputa — `amount` = `disputed_amount`, que no formato antigo já era 0 para
 * ganha/`warning_*`/`prevented`, e essas classes contam 0 de qualquer forma.
 *
 * Origem: #740 — 2026-09-25 23:29 +0200.
 *
 * Usado por: `summarizePaymentDisputes` (este módulo); Functions
 * `webhooks/handleChargeDispute.ts` (lê o estado anterior antes de aplicar o
 * evento). `LEGACY_DISPUTE_ID` é o id que o handler usa para MIGRAR o formato
 * antigo para o mapa na primeira escrita.
 */
export function paymentDisputeEntries(payment: PaymentDisputeFields | undefined | null): IdentifiedDisputeEntry[] {
  if (!payment) return [];
  const map = payment.disputes;
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    const out: IdentifiedDisputeEntry[] = [];
    for (const [id, raw] of Object.entries(map as Record<string, unknown>)) {
      const parsed = PaymentDisputeEntrySchema.safeParse(raw);
      if (parsed.success) out.push({ id, ...parsed.data });
    }
    return out;
  }
  const status = typeof payment.dispute_status === 'string' ? payment.dispute_status : null;
  const amount = typeof payment.disputed_amount === 'number' ? payment.disputed_amount : null;
  if (status === null && amount === null) return [];
  return [
    {
      id: typeof payment.dispute_id === 'string' ? payment.dispute_id : LEGACY_DISPUTE_ID,
      // Formato antigo com valor e sem status não existe em produção; se
      // existir, é valor retido — cai do lado do estorno, como status novo.
      status: status ?? 'needs_response',
      amount: Math.max(0, amount ?? 0),
      updated_at: typeof payment.dispute_updated_at === 'number' ? payment.dispute_updated_at : 0,
    },
  ];
}

export type PaymentDisputeSummary = {
  /** Valor retido pelas disputas ABERTAS e PERDIDAS (cents), com teto no
   *  `amount` do payment. É o `disputed_amount` gravado. */
  withdrawnCents: number;
  /** Só o das PERDIDAS (cents), com teto no `amount`: estorno definitivo. */
  lostCents: number;
  /** Só o das ABERTAS (cents), com teto no `amount`: estorno reversível. */
  openCents: number;
  /** Há disputa com dinheiro retido e desfecho pendente. */
  hasOpen: boolean;
  /** `dispute_status` agregado (ver `AGGREGATE_PRIORITY`); `null` sem disputa. */
  aggregateStatus: string | null;
  /** A disputa que deu o status agregado; `null` sem disputa. */
  aggregateDisputeId: string | null;
  /** Maior `updated_at` entre as disputas; `null` sem disputa. */
  latestUpdatedAt: number | null;
};

/**
 * Resumo das disputas do payment: o que está retido, o que está perdido, se há
 * desfecho pendente e o status agregado. Os valores somam as disputas (duas
 * disputas são dois valores retidos) e param no `amount` do payment — nunca se
 * devolve mais do que se cobrou.
 *
 * Contrato: puro. Sem `amount` no payment o teto é infinito (doc cru sem o
 * campo não deve zerar o retido). Os `*Cents` são o que o handler grava em
 * `disputed_amount` (`withdrawnCents`) e `dispute_status` (`aggregateStatus`).
 *
 * Origem: #740 — 2026-09-25 23:29 +0200.
 *
 * Usado por: Functions `webhooks/handleChargeDispute.ts` (deriva os campos
 * planos); CRM Functions `lib/commissionCycle.ts` (`withdrawnCents` vs
 * `refunded`, o MAIOR dos dois) e `lib/appPayments.ts`; `schemas.ts`.
 */
export function summarizePaymentDisputes(
  payment: PaymentDisputeFields | undefined | null,
): PaymentDisputeSummary {
  const entries = paymentDisputeEntries(payment);
  const cap =
    payment && typeof payment.amount === 'number' ? Math.max(0, payment.amount) : Number.POSITIVE_INFINITY;
  let open = 0;
  let lost = 0;
  let best: IdentifiedDisputeEntry | null = null;
  let latest: number | null = null;
  for (const e of entries) {
    const cls = classifyDisputeStatus(e.status);
    if (cls === 'OPEN') open += e.amount;
    if (cls === 'LOST') lost += e.amount;
    latest = latest === null ? e.updated_at : Math.max(latest, e.updated_at);
    if (
      best === null ||
      AGGREGATE_PRIORITY[cls] > AGGREGATE_PRIORITY[classifyDisputeStatus(best.status)] ||
      (AGGREGATE_PRIORITY[cls] === AGGREGATE_PRIORITY[classifyDisputeStatus(best.status)] &&
        e.updated_at > best.updated_at)
    ) {
      best = e;
    }
  }
  const lostCents = Math.min(cap, lost);
  const openCents = Math.min(cap, open);
  return {
    withdrawnCents: Math.min(cap, open + lost),
    lostCents,
    openCents,
    hasOpen: openCents > 0,
    aggregateStatus: best?.status ?? null,
    aggregateDisputeId: best?.id ?? null,
    latestUpdatedAt: latest,
  };
}
